import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Trash2, Pencil, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Simple emoji picker - common emojis
const COMMON_EMOJIS = [
  '🏓', '🧘', '🏃', '🚴', '🏸', '⚽', '🏀', '🎾', '🏊', '⛷️',
  '🎤', '🎧', '🎙️', '🎷', '🎸', '🎹', '🎺', '🎻', '🎼', '🎵',
  '🎨', '📸', '🏺', '🖼️', '✏️', '🖌️', '🎭', '🎪', '🎬', '🎥',
  '🍔', '👨‍🍳', '🍷', '🌮', '🥐', '🍕', '🍜', '🍣', '🍰', '☕',
  '🤝', '😂', '📚', '🧠', '💬', '🎯', '🎊', '🎉', '🎈', '🎀',
  '🎲', '🎮', '🕹️', '🐉', '❓', '🃏', '🎰', '🎪', '🏆', '🥇',
  '🎬', '🎥', '📹', '🌃', '🎞️', '📽️', '🎦', '📺', '📻', '🎙️',
  '💃', '🌆', '🎭', '🏠', '🏖️', '🎪', '🎉', '🎊', '🎈', '🎁'
];

export default function CategoryManager({ credentials }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingL1, setEditingL1] = useState(null);
  const [editingL2, setEditingL2] = useState(null);
  const [isL1DialogOpen, setIsL1DialogOpen] = useState(false);
  const [isL2DialogOpen, setIsL2DialogOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  
  const [l1Form, setL1Form] = useState({ id: '', name: '', emoji: '⚽' });
  const [l2Form, setL2Form] = useState({ id: '', name: '', emoji: '🎉', parentId: '' });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateL1 = async () => {
    if (!l1Form.id || !l1Form.name) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        },
        body: JSON.stringify({
          id: l1Form.id,
          name: l1Form.name,
          emoji: l1Form.emoji,
          l2_categories: []
        })
      });

      if (response.ok) {
        toast.success('Category created');
        setIsL1DialogOpen(false);
        setL1Form({ id: '', name: '', emoji: '⚽' });
        fetchCategories();
      }
    } catch (error) {
      toast.error('Failed to create category');
    }
  };

  const handleDeleteL1 = async (categoryId) => {
    if (!confirm('Delete this category? All L2 categories will be deleted.')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/categories/${categoryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        toast.success('Category deleted');
        fetchCategories();
      }
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  const handleCreateL2 = async () => {
    if (!l2Form.id || !l2Form.name || !l2Form.parentId) {
      toast.error('Please fill all fields');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/categories/${l2Form.parentId}/l2`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        },
        body: JSON.stringify({
          id: l2Form.id,
          name: l2Form.name,
          emoji: l2Form.emoji
        })
      });

      if (response.ok) {
        toast.success('L2 category added');
        setIsL2DialogOpen(false);
        setL2Form({ id: '', name: '', emoji: '🎉', parentId: '' });
        fetchCategories();
      }
    } catch (error) {
      toast.error('Failed to add L2 category');
    }
  };

  const handleDeleteL2 = async (categoryId, l2Id) => {
    if (!confirm('Delete this L2 category?')) return;

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/categories/${categoryId}/l2/${l2Id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': 'Basic ' + btoa(`${credentials.username}:${credentials.password}`)
        }
      });

      if (response.ok) {
        toast.success('L2 category deleted');
        fetchCategories();
      }
    } catch (error) {
      toast.error('Failed to delete L2 category');
    }
  };

  const toggleCategory = (categoryId) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  if (loading) {
    return <div className="text-center py-8">Loading categories...</div>;
  }

  return (
    <div className="space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto pr-2 scrollbar-styled">
      <div className="flex justify-between items-center sticky top-0 bg-background z-10 pb-4">
        <h2 className="text-2xl font-bold">Category Management</h2>
        <Dialog open={isL1DialogOpen} onOpenChange={setIsL1DialogOpen}>
          <DialogTrigger asChild>
            <Button className="gradient-primary">
              <Plus className="h-4 w-4 mr-2" />
              Add L1 Category
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create L1 Category</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Category ID</Label>
                <Input
                  placeholder="e.g., sports"
                  value={l1Form.id}
                  onChange={(e) => setL1Form({ ...l1Form, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
                />
              </div>
              <div>
                <Label>Category Name</Label>
                <Input
                  placeholder="e.g., Sports"
                  value={l1Form.name}
                  onChange={(e) => setL1Form({ ...l1Form, name: e.target.value })}
                />
              </div>
              <div>
                <Label>Category Emoji</Label>
                <div className="flex items-center gap-2">
                  <Input
                    value={l1Form.emoji}
                    onChange={(e) => setL1Form({ ...l1Form, emoji: e.target.value })}
                    className="w-20 text-center text-2xl"
                    maxLength={2}
                  />
                  <div className="flex-1 grid grid-cols-10 gap-1 max-h-32 overflow-y-auto p-2 border rounded">
                    {COMMON_EMOJIS.map((emoji, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => setL1Form({ ...l1Form, emoji })}
                        className="text-xl hover:bg-muted p-1 rounded"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <Button onClick={handleCreateL1} className="w-full">Create Category</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 gap-4">
        {categories.map((category) => (
          <Card key={category._id} className="overflow-hidden">
            <CardHeader className="bg-muted/50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => toggleCategory(category._id)}
                  >
                    {expandedCategories.has(category._id) ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                  <CardTitle className="text-lg">{category.name}</CardTitle>
                  <span className="text-sm text-muted-foreground">
                    ({category.l2_categories?.length || 0} subcategories)
                  </span>
                </div>
                <div className="flex space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setL2Form({ ...l2Form, parentId: category._id });
                      setIsL2DialogOpen(true);
                    }}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add L2
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleDeleteL1(category._id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            {expandedCategories.has(category._id) && (
              <CardContent className="pt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {category.l2_categories?.map((l2) => (
                    <div
                      key={l2.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center space-x-2">
                        <span className="text-2xl">{l2.emoji}</span>
                        <div>
                          <p className="font-medium">{l2.name}</p>
                          <p className="text-xs text-muted-foreground">{l2.id}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteL2(category._id, l2.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* L2 Category Dialog */}
      <Dialog open={isL2DialogOpen} onOpenChange={setIsL2DialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add L2 Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>L2 Category ID</Label>
              <Input
                placeholder="e.g., pickleball"
                value={l2Form.id}
                onChange={(e) => setL2Form({ ...l2Form, id: e.target.value.toLowerCase().replace(/\s/g, '_') })}
              />
            </div>
            <div>
              <Label>L2 Category Name</Label>
              <Input
                placeholder="e.g., Pickleball"
                value={l2Form.name}
                onChange={(e) => setL2Form({ ...l2Form, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Emoji</Label>
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-4xl">{l2Form.emoji}</span>
                <Input
                  value={l2Form.emoji}
                  onChange={(e) => setL2Form({ ...l2Form, emoji: e.target.value })}
                  className="w-20"
                />
              </div>
              <div className="grid grid-cols-10 gap-2 max-h-40 overflow-y-auto p-2 border rounded">
                {COMMON_EMOJIS.map((emoji) => (
                  <button
                    key={emoji}
                    onClick={() => setL2Form({ ...l2Form, emoji })}
                    className="text-2xl hover:bg-muted p-1 rounded transition-smooth"
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <Button onClick={handleCreateL2} className="w-full">Add L2 Category</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
